import AddressAutocomplete from './AddressAutocomplete';
import { singleLocationAudience } from '@/services/audienceService';
import UserAudienceFields from '@/components/audience/UserAudienceFields';
import type { UserAudience } from '@/services/audienceService';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, useRolesForDropdown, useDeleteUser, useAuth, useConfirmDialog, useBranding } from '@/hooks';
import toast from 'react-hot-toast';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { CreateUserRequest, UpdateUserRequest, User, UserRoleObject } from '@/types';
import ApiSelect from '@/components/ui/ApiSelect.tsx';
import { useNavigate } from 'react-router-dom';
import Toggle from '@/components/ui/Toggle.tsx';

const createUserSchema = (t: Translation, isEdit: boolean = false) => z.object({
  username: z
    .string()
    .min(3, t.validation.minLength(3))
    .max(50, t.validation.maxLength(50))
    .regex(/^[a-zA-Z0-9_]+$/, t.validation.usernameFormat),
  fullName: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(100, t.validation.maxLength(100)),
  email: z
    .union([
      z.email({ message: t.validation.email }),
      z.literal(''),
    ])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  phoneNumber: z
    .union([
      z
        .string()
        .min(8, t.validation.minLength(8))
        .max(20, t.validation.maxLength(20))
        .regex(/^[+]?[\d\s\-()]+$/, t.validation.phoneFormat),
      z.literal(''),
    ])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressCountryId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressAdminAreaId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressLocalityId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  address: z
    .string()
    .max(255, t.validation.maxLength(255))
    .optional(),
  comment: z
    .string()
    .max(1000, t.validation.maxLength(1000))
    .optional(),
  password: isEdit
    ? z.string().optional()
    : z
      .string()
      .min(4, t.validation.minLength(8))
      .max(128, t.validation.maxLength(128)),
  roleCode: z.string(t.users.validation.roleRequired),
  expirationDate: z.string().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']).optional(),
  pricePer12Credits: z
    .string()
    .optional()
    .refine((value) => value === undefined || value === '' || (!Number.isNaN(Number(value)) && Number(value) >= 0), {
      message: 'El precio no puede ser negativo',
    }),
  pricePer12CreditsCurrency: z.enum(['USD', 'DOP']).optional(),
});

type UserFormData = z.infer<ReturnType<typeof createUserSchema>>;

interface UserFormProps {
  onSuccess?: (data: CreateUserRequest | UpdateUserRequest) => void;
  initialData?: User;
  isEdit?: boolean;
  isSubmitting: boolean;
  onCancel?: () => void;
}

const UserForm = ({ onSuccess, onCancel, initialData, isEdit = false, isSubmitting }: UserFormProps) => {
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const [audience, setAudience] = useState<UserAudience>(singleLocationAudience(initialData?.audience));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { branding } = useBranding();
  const deleteUserMutation = useDeleteUser();
  const { user } = useAuth();
  const navigate = useNavigate();

  const defaultValues = useMemo(
    () => ({
      username: initialData?.username || '',
      fullName: initialData?.fullName || '',
      email: initialData?.email || '',
      phoneNumber: initialData?.phoneNumber || '',
      address: initialData?.address || '',
      addressCountryId: initialData?.addressCountryId || '',
      addressAdminAreaId: initialData?.addressAdminAreaId || '',
      addressLocalityId: initialData?.addressLocalityId || '',
      comment: initialData?.comment || '',
      roleCode: initialData?.roleCode ?? initialData?.role?.code ?? '',
      expirationDate: initialData?.expirationDate || '',
      status: initialData?.status || 'ACTIVE',
      pricePer12Credits: initialData?.pricePer12Credits?.toString() || '',
      pricePer12CreditsCurrency: initialData?.pricePer12CreditsCurrency || 'USD',
    }),
    [initialData]
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<UserFormData>({
    resolver: zodResolver(createUserSchema(t, isEdit)),
    defaultValues
  });

  const statusValue = watch('status');
  const roleCodeValue = watch('roleCode');
  const showCreditPricingFields = roleCodeValue === 'agent' || roleCodeValue === 'superagent';

  const lastInitializedUserIdRef = useRef<number | null>(null);
  useEffect(() => {
    const currentUserId = initialData?.id ?? null;
    if (lastInitializedUserIdRef.current === currentUserId) {
      return;
    }

    lastInitializedUserIdRef.current = currentUserId;
    reset(defaultValues);
  }, [defaultValues, initialData?.id, reset]);

  const handleDeleteUser = async () => {
    if(!initialData) return;

    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.users.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteUserMutation.mutateAsync(initialData.id);
          toast.success(t.users.messages.deleted);
          navigate('/users');
        },
      });

      if (!confirmed) {
        return;
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    setSubmitError(null);
    const normalizedPricePer12Credits = data.pricePer12Credits ? Number(data.pricePer12Credits) : undefined;

    try {
      if (isEdit) {
        const updateData: UpdateUserRequest = {
          audience: audienceManagementEnabled ? singleLocationAudience(audience) : undefined,
          username: data.username,
          fullName: data.fullName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          address: data.address,
          addressCountryId: audienceManagementEnabled ? (data.addressCountryId ?? '') : undefined,
          addressAdminAreaId: audienceManagementEnabled ? (data.addressAdminAreaId ?? '') : undefined,
          addressLocalityId: audienceManagementEnabled ? (data.addressLocalityId ?? '') : undefined,
          comment: data.comment,
          roleCode: data.roleCode,
          expirationDate: data.expirationDate,
          status: data.status,
          password: data.password != "" ? data.password : undefined,
          pricePer12Credits: showCreditPricingFields ? normalizedPricePer12Credits : undefined,
          pricePer12CreditsCurrency: showCreditPricingFields ? data.pricePer12CreditsCurrency : undefined,
        };
        await onSuccess?.(updateData);
      } else {
        const createData: CreateUserRequest = {
          audience: audienceManagementEnabled ? singleLocationAudience(audience) : undefined,
          username: data.username,
          fullName: data.fullName,
          email: data.email,
          phoneNumber: data.phoneNumber!,
          address: data.address,
          addressCountryId: data.addressCountryId,
          addressAdminAreaId: data.addressAdminAreaId,
          addressLocalityId: data.addressLocalityId,
          comment: data.comment,
          password: data.password!,
          roleCode: data.roleCode,
          pricePer12Credits: showCreditPricingFields ? normalizedPricePer12Credits : undefined,
          pricePer12CreditsCurrency: showCreditPricingFields ? data.pricePer12CreditsCurrency : undefined,
        };
        await onSuccess?.(createData);
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const mapRolesToOptions = (data: unknown[]) =>
    (data as UserRoleObject[]).map(role => ({ value: role.code, label: role.name }));

  const audienceManagementEnabled = Boolean(branding.modules?.audienceManagementEnabled);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Editar Usuario' : t.createUser.title}
          </h2>
          <Toggle
            variant="greenRed"
            size="lg"
            label="Activo"
            checked={statusValue === 'ACTIVE'}
            onChange={(checked) => setValue('status', checked ? 'ACTIVE' : 'INACTIVE')}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={t.form.username}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            placeholder={t.placeholders.username}
            error={errors.username?.message}
            {...register('username')}
          />

          <Input
            label={t.form.fullName}
            placeholder={t.placeholders.fullName}
            error={errors.fullName?.message}
            {...register('fullName')}
          />

          <Input
            label={t.form.email}
            type="email"
            placeholder={t.placeholders.email}
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label={t.form.phoneNumber}
            type="tel"
            placeholder={t.placeholders.phoneNumber}
            error={errors.phoneNumber?.message}
            {...register('phoneNumber')}
          />

          <Input
            label={t.form.password}
            type="password"
            placeholder={t.placeholders.password}
            error={errors.password?.message}
            helperText={t.helpers.password}
            {...register('password')}
          />

          <Controller
            control={control}
            name="roleCode"
            render={({ field }) => (
              <ApiSelect
                label={t.form.role}
                placeholder={t.placeholders.selectRole}
                useQueryHook={useRolesForDropdown}
                mapDataToOptions={mapRolesToOptions}
                error={errors.roleCode?.message}
                {...field}
              />
            )}
          />

          {showCreditPricingFields && (
            <Input
              label="Precio por 12 créditos"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              error={errors.pricePer12Credits?.message}
              {...register('pricePer12Credits')}
            />
          )}

          {showCreditPricingFields && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Moneda
              </label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                {...register('pricePer12CreditsCurrency')}
              >
                <option value="USD">USD</option>
                <option value="DOP">DOP</option>
              </select>
              {errors.pricePer12CreditsCurrency && (
                <p className="mt-1 text-sm text-red-600">{errors.pricePer12CreditsCurrency.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          {audienceManagementEnabled && <UserAudienceFields value={audience} onChange={setAudience} disabled={isSubmitting} />}
          {audienceManagementEnabled && <AddressAutocomplete
            value={{ countryId: watch('addressCountryId') ?? '', adminAreaId: watch('addressAdminAreaId') ?? '', localityId: watch('addressLocalityId') ?? '' }}
            disabled={isSubmitting}
            error={errors.addressCountryId?.message || errors.addressAdminAreaId?.message || errors.addressLocalityId?.message}
            onChange={value => {
              setValue('addressCountryId', value.countryId, { shouldDirty: true });
              setValue('addressAdminAreaId', value.adminAreaId, { shouldDirty: true });
              setValue('addressLocalityId', value.localityId, { shouldDirty: true });
            }} />}

          <Input
            label="Dirección"
            placeholder="Ingresa la dirección del usuario (opcional)"
            error={errors.address?.message}
            {...register('address')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comentarios
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={4}
              placeholder="Comentarios adicionales sobre el usuario (opcional)"
              {...register('comment')}
            />
            {errors.comment && (
              <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
            )}
          </div>
        </div>
        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">

          {isEdit && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteUser}
              disabled={isSubmitting || user?.username === initialData?.username}
            >
              {t.buttons.delete}
            </Button>
          )}

          <div className="flex justify-end space-x-4 items-center">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t.buttons.cancel}
              </Button>
            )}
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? (isEdit ? 'Actualizando...' : t.buttons.creating)
                : (isEdit ? 'Actualizar' : t.buttons.create)
              }
            </Button>
          </div>

        </div>
      </form>
      {dialog}
    </div>
  );
};

export default UserForm;
